import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConversationHistoryService, ConversationEntry } from '../services/conversation-history.service';
import { GlobalServicesService } from '../services/global-services.service';
import { CopilotService } from './copilot.service';
import { CountryStateCityService } from '../services/country-state-city.service';

@Component({
    selector: 'app-copilot',
    templateUrl: './copilot.component.html',
    styleUrls: ['./copilot.component.css'],
    standalone: false
})
export class CopilotComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  hasUserInteracted = false;
  userPrivileges: Set<string> = new Set();
  userName = 'Paul';
  userInput = '';
  selectedPurpose = '';
  messages: { type: 'user' | 'ai', content: string, safeContent?: SafeHtml }[] = [];
  previewContent = '';
  apiResponse: any = null;
  isLoading = false;
  loadingStep = 1;
  totalSteps = 4;
  loadingPercentage = 0;
  loadingInterval: any;
  showRestartConfirmation = false;
  showLimitedOptionsMessage = false;
  isCreating = false;
  creationStatus: { type: 'success' | 'error', message: string } | null = null;
  showEditHelper = false;
  editHelperText = 'Please enter your changes in the input field below and press Enter.';
  hireApiRes: any = null;
  showJobCreationConfirmation = false;
  jobApiRes: any = null; // Add property to store job response
  userId = '';
  companyUUID = '';
  isEditing = false;
  sidebarWidth = 450; // Default sidebar width
  isResizing = false;
  isUpdating = false;
  editForm!: FormGroup;
  initialFormValues: any = null; // Store initial form values to track changes

  // Form data for dropdowns
  eventCategories: Array<any> = [];
  eventTypes: Array<any> = [];
  institutions: Array<any> = [];
  jobRoles: Array<any> = [];
  regForms: Array<any> = [];
  emailTemplates: Array<any> = [];
  roleTypes: Array<any> = [];
  workModes = [
    { label: 'Remote', value: 'Remote' },
    { label: 'In Office', value: 'Office' },
    { label: 'Hybrid', value: 'Hybrid' },
  ];
  countries: Array<any> = [];
  states: Array<any> = [];
  cities: Array<any> = [];
  jsonData: any = {};

  // Copilot icon rotation based on cursor movement
  copilotRotation = 0;
  lastMouseX = 0;
  rotationTimeout: any;

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    private conversationHistory: ConversationHistoryService,
    private globalService: GlobalServicesService,
    private copilotService: CopilotService,
    private formBuilder: FormBuilder,
    private countryStateCityService: CountryStateCityService
  ) {}

  ngOnInit() {
    this.globalService.userMeta$.subscribe(meta => {
      if (meta?.user_profile) {
        this.userName = meta.user_profile.first_name || 'Paul';        
        const email = meta.user_profile.email || '';
        this.userId = email ? btoa(email) : '';
      } else {
        this.userName = 'Paul';
        this.userId = '';
      }
    });
    this.companyUUID = localStorage.getItem('companyUUID') || '';
    this.globalService.privilegeDetailsSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((privileges) => {
        this.userPrivileges = privileges;
      });
    this.initializeEditForm();
  }

  initializeEditForm() {
    this.editForm = this.formBuilder.group({
      // Event fields
      eventName: [''],
      startDate: [''],
      endDate: [''],
      registrationStartDate: [''],
      registrationEndDate: [''],
      eventCategory: [''],
      eventType: [''],
      institutionName: [[]],
      institutionAddress: [''],
      jobRoles: [[]],
      workExperience: [0],
      minSalary: [0],
      maxSalary: [0],
      opening: [0],
      maxApplicant: [0],
      
      // Job fields
      jobTitle: [''],
      roleType: [''],
      workMode: [''],
      country: [''],
      state: [''],
      city: [''],
      businessUnit: [''],
      description: ['']
    });
  }

  ngOnDestroy() {
    this.stopLoadingAnimation();
    document.removeEventListener('mousemove', this.handleResize.bind(this));
    document.removeEventListener('mouseup', this.stopResize.bind(this));
    if (this.rotationTimeout) {
      clearTimeout(this.rotationTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMouseMove(event: MouseEvent) {
    const currentX = event.clientX;
    
    if (this.lastMouseX === 0) {
      this.lastMouseX = currentX;
      return;
    }
    
    const deltaX = currentX - this.lastMouseX;
    
    if (Math.abs(deltaX) > 3) {
      this.copilotRotation += deltaX * 0.5;
      
      this.lastMouseX = currentX;
      
      if (this.rotationTimeout) {
        clearTimeout(this.rotationTimeout);
      }
      this.rotationTimeout = setTimeout(() => {
        this.copilotRotation = 0;
      }, 1500);
    }
  }

  selectPurpose(purpose: string) {
    this.selectedPurpose = purpose;
    this.showLimitedOptionsMessage = false;
    this.handlePurposeSelection(purpose);
  }

  private handlePurposeSelection(purpose: string) {
    switch (purpose) {
      case 'createJob':
        this.initializeJobCreation();
        break;
      case 'createEvent':
        this.initializeEventCreation();
        break;
      case 'listJobs':
        this.listJobs();
        break;
      case 'listEvents':
        this.listEvents();
        break;
    }
  }

  private sanitizeMessageContent(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  async submitInput() {
    if (this.isLoading || this.isCreating || this.isUpdating) {
      return;
    }

    if (!this.userInput.trim()) {
      return;
    }

    this.hasUserInteracted = true;
    this.showEditHelper = false;
    this.isEditing = false;
    const input = this.userInput.trim();
    
    this.userInput = '';
    
    if (!this.selectedPurpose) {
      this.showLimitedOptionsMessage = true;
      this.messages.push({
        type: 'ai',
        content: 'Please select what you want to do by clicking one of the cards below. Your input will be preserved.',
        safeContent: this.sanitizeMessageContent('Please select what you want to do by clicking one of the cards below. Your input will be preserved.')
      });
      setTimeout(() => { this.userInput = input; }, 0);
      return;
    }
    
    if (this.isGreeting(input)) {
      this.showLimitedOptionsMessage = false;
      this.messages.push({
        type: 'user',
        content: input,
        safeContent: this.sanitizeMessageContent(input)
      });
      this.messages.push({
        type: 'ai',
        content: this.getGreetingResponse(input),
        safeContent: this.sanitizeMessageContent(this.getGreetingResponse(input))
      });
      return;
    }
    
    this.showLimitedOptionsMessage = false;
    this.messages.push({
      type: 'user',
      content: input,
      safeContent: this.sanitizeMessageContent(input)
    });
    
    await this.handleUserInput(input);
  }

  private isGreeting(input: string): boolean {
    const greetings = ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings'];
    const normalized = input.trim().toLowerCase();
    return greetings.some(greet => normalized === greet || normalized.startsWith(greet + ' '));
  }

  private getGreetingResponse(input: string): string {
    return `Hello! How can I assist you today? I can help you create or manage jobs and events.`;
  }


  private async handleUserInput(input: string) {
    this.isLoading = true;
    this.startLoadingAnimation();
    try {
      const type = this.selectedPurpose === 'createEvent' ? 'event' : this.selectedPurpose === 'createJob' ? 'job' : null;
      let previousMessages: { role: 'user' | 'ai', content: string }[] = [];
      if (type) {
        const prev = await this.conversationHistory.getLatestConversation(this.companyUUID, this.userId, type);
        if (prev && prev.messages) {
          previousMessages = prev.messages as { role: 'user' | 'ai', content: string }[];
          const maxHistoryMessages = 10;
          if (previousMessages.length > maxHistoryMessages) {
            previousMessages = previousMessages.slice(-maxHistoryMessages);
          }
        }
      }
      
      const allMessages: { role: 'user' | 'ai', content: string }[] = [
        ...previousMessages,
        { role: 'user', content: input }
      ];
      

      const promptToSend = this.apiResponse
        ? this.buildDraftEditPrompt(input)
        : this.buildPromptWithHistory(allMessages);

      let response;
      if (this.selectedPurpose === 'createEvent') {
        response = await firstValueFrom(this.copilotService.generateEventViaAI(promptToSend));
      } else if (this.selectedPurpose === 'createJob') {
        response = await firstValueFrom(this.copilotService.generateJob(promptToSend));
      }
      
      this.apiResponse = response;
      this.messages.push({
        type: 'ai',
        content: 'Here\'s what I generated based on your input:',
        safeContent: this.sanitizeMessageContent('Here\'s what I generated based on your input:')
      });
      
      if (type) {
        const entry: ConversationEntry = {
          companyUUID: this.companyUUID,
          userId: this.userId,
          type,
          timestamp: Date.now(),
          messages: [
            ...allMessages.slice(-10),
            { role: 'ai', content: JSON.stringify(response) }
          ]
        };
        await this.conversationHistory.appendToConversation(entry);
      }
    } catch (error) {
      this.messages.push({
        type: 'ai',
        content: 'Sorry, I encountered an error while processing your request.',
        safeContent: this.sanitizeMessageContent('Sorry, I encountered an error while processing your request.')
      });
    } finally {
      this.stopLoadingAnimation();
      this.isLoading = false;
    }
  }

  private startLoadingAnimation() {
    this.loadingStep = 1;
    this.loadingPercentage = 0;
    
    const stepIntervals = [
      { target: 25, speed: 200, step: 2 },
      { target: 60, speed: 300, step: 3 },
      { target: 85, speed: 500, step: 4 },
      { target: 95, speed: 800, step: 4 }
    ];
    
    let currentInterval = 0;
    
    const updateLoading = () => {
      const interval = stepIntervals[currentInterval];
      
      if (this.loadingPercentage < interval.target) {
        this.loadingPercentage += 1;
        
        if (this.loadingPercentage === interval.target && interval.step !== this.loadingStep) {
          this.loadingStep = interval.step;
        }
      } else if (currentInterval < stepIntervals.length - 1) {
        currentInterval++;
        clearInterval(this.loadingInterval);
        this.loadingInterval = setInterval(updateLoading, stepIntervals[currentInterval].speed);
      }
    };
    
    this.loadingInterval = setInterval(updateLoading, stepIntervals[0].speed);
  }

  private stopLoadingAnimation() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    }
    
    this.loadingPercentage = 100;
    this.loadingStep = 4;
  }

  initializeJobCreation() {
    this.hasUserInteracted = true;
    this.previewContent = 'Please describe the job you want to create';
  }

  initializeEventCreation() {
    this.hasUserInteracted = true;
    this.previewContent = 'Please describe the event you want to create';
  }

  listJobs() {
    this.hasUserInteracted = true;
    this.navigateToJobs();
  }

  listEvents() {
    this.hasUserInteracted = true;
    this.navigateToEvents();
  }

  getLocationDetails(location: any): string {
    return this.copilotService.getLocationDetails(location);
  }

  getRoleTypeName(roleTypeId: number): string {
    return this.copilotService.getRoleTypeName(roleTypeId);
  }

  getLoadingMessage(): string {
    if (this.selectedPurpose === 'createEvent') {
      switch(this.loadingStep) {
        case 1: return 'Analyzing event requirements...';
        case 2: return 'Generating event structure...';
        case 3: return 'Finalizing event details...';
        case 4: return 'Almost ready with your event...';
        default: return 'Generating event details...';
      }
    } else if (this.selectedPurpose === 'createJob') {
      switch(this.loadingStep) {
        case 1: return 'Analyzing job requirements...';
        case 2: return 'Generating job description...';
        case 3: return 'Finalizing position details...';
        case 4: return 'Almost ready with your job posting...';
        default: return 'Generating job details...';
      }
    }
    return 'Processing your request...';
  }

  restartConversation() {
    this.showRestartConfirmation = true;
  }
  
  async confirmRestart() {
    this.showRestartConfirmation = false;
    this.stopLoadingAnimation();
    await this.conversationHistory.clearConversations(this.companyUUID, this.userId);
    this.hasUserInteracted = false;
    this.messages = [];
    this.userInput = '';
    this.selectedPurpose = '';
    this.previewContent = '';
    this.apiResponse = null;
    this.hireApiRes = null;
    this.jobApiRes = null;
    this.isLoading = false;
    this.loadingStep = 1;
    this.loadingPercentage = 0;
    this.showLimitedOptionsMessage = false;
    this.showEditHelper = false;
    this.isEditing = false;
    this.creationStatus = null;
  }

  createEventNow() {
    this.isCreating = true;
    this.creationStatus = null;
    
    let responseToFormat = { ...this.apiResponse };
    
    if (this.editForm && this.editForm.touched) {
      const formValues = this.editForm.value;
      
      if (formValues.institutionName !== undefined) {
        if (Array.isArray(formValues.institutionName) && formValues.institutionName.length > 0) {
          responseToFormat.institutes = formValues.institutionName.map((id: any) => ({ id }));
        } else if (formValues.institutionName && !Array.isArray(formValues.institutionName)) {
          responseToFormat.institutes = [{ id: formValues.institutionName }];
        }
      }
      
      if (formValues.institutionAddress) {
        responseToFormat.institution_address = formValues.institutionAddress;
      }
    }
    
    const eventPayload = this.copilotService.formatEventPayload(responseToFormat);
    
    firstValueFrom(this.copilotService.submitEvent(eventPayload))
      .then((response: any) => {
        this.hireApiRes = response;
        this.creationStatus = {
          type: 'success',
          message: 'Event created successfully! You can now view it in your events list.'
        };
      })
        .catch((error: any) => {
          let errorMessage = 'Failed to create event. Please try again or check the details.';
          
          if (error?.error?.detail) {
            if (Array.isArray(error.error.detail)) {
              const errorMessages = error.error.detail.map((err: any) => err.msg || err).filter(Boolean);
              if (errorMessages.length > 0) {
                errorMessage = errorMessages.join('. ');
              }
            } else if (typeof error.error.detail === 'string') {
              errorMessage = error.error.detail;
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }
          
          this.creationStatus = {
            type: 'error',
            message: errorMessage
          };
        })
      .finally(() => {
        this.isCreating = false;
      });
  }
  
  editEventDetails() {
    this.isEditing = true;
    this.creationStatus = null;
    this.loadFormDataForEvents().then(() => {
      this.populateEditForm();
    });
  }

  createJobNow() {
    this.isCreating = true;
    this.creationStatus = null;
    
    let responseToFormat = { ...this.apiResponse };
    
    if (this.editForm && this.editForm.touched) {
      const formValues = this.editForm.value;
      
      if (!responseToFormat.locations || !Array.isArray(responseToFormat.locations)) {
        responseToFormat.locations = [];
      }
      
      const locationData = {
        country: formValues.country || '',
        state: formValues.state || '',
        city: formValues.city || '',
        business_unit: formValues.businessUnit || 'Demo Unit',
        opening: formValues.opening !== undefined && formValues.opening !== null ? formValues.opening : (responseToFormat.locations[0]?.opening || 1),
        max_applicant: formValues.maxApplicant !== undefined && formValues.maxApplicant !== null ? formValues.maxApplicant : (responseToFormat.locations[0]?.max_applicant || 10),
        diversity_male: responseToFormat.locations[0]?.diversity_male || 0,
        diversity_female: responseToFormat.locations[0]?.diversity_female || 0,
        diversity_other: responseToFormat.locations[0]?.diversity_other || 0
      };
      
      if (responseToFormat.locations.length > 0) {
        responseToFormat.locations[0] = { ...responseToFormat.locations[0], ...locationData };
      } else {
        responseToFormat.locations.push(locationData);
      }
      
      if (formValues.jobTitle) responseToFormat.name = formValues.jobTitle;
      if (formValues.roleType) responseToFormat.role_type_id = formValues.roleType;
      if (formValues.workMode) responseToFormat.work_mode = formValues.workMode;
      if (formValues.description) responseToFormat.description = formValues.description;
    }
    
    const jobPayload = this.copilotService.formatJobPayload(responseToFormat);
    
    firstValueFrom(this.copilotService.submitJob(jobPayload))
      .then(response => {
        this.jobApiRes = response;
        this.creationStatus = {
          type: 'success',
          message: 'Job created successfully! You can now view it in your jobs list.'
        };
      })
       .catch((error: any) => {
          let errorMessage = 'Failed to create job. Please try again or check the details.';
          
          if (error?.error?.detail) {
            if (Array.isArray(error.error.detail)) {
              const errorMessages = error.error.detail.map((err: any) => err.msg || err).filter(Boolean);
              if (errorMessages.length > 0) {
                errorMessage = errorMessages.join('. ');
              }
            } else if (typeof error.error.detail === 'string') {
              errorMessage = error.error.detail;
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }
          
          this.creationStatus = {
            type: 'error',
            message: errorMessage
          };
        })
      .finally(() => {
        this.isCreating = false;
      });
  }
  
  editJobDetails() {
    this.isEditing = true;
    this.creationStatus = null; 
    this.loadFormDataForJobs().then(() => {
      this.populateEditForm();
    });
  }

  navigateToEvents() {
    this.router.navigate(['/events']);
  }

  navigateToJobs() {
    this.router.navigate(['/roles']);
  }

  navigateToEventDetails() {
    if (this.hireApiRes?.result?.name) {
      const eventName = this.hireApiRes.result.name;
      const eventId = this.hireApiRes.result.id;
      // Store necessary data in localStorage
      window.localStorage.setItem('selectedEventId', eventId);
      window.localStorage.setItem('selectedEventName', eventName);
      window.localStorage.setItem('selectedJobId', eventId);
      window.localStorage.setItem('eventCategory', 'technical');
      
      this.router.navigate(['events/', eventId], {
        queryParams: { view: 'eventDetails' }
      });
    }
  }

  confirmJobCreationNavigation() {
    this.showJobCreationConfirmation = false;
    this.router.navigate(['/roles']);
  }

  cancelJobCreationNavigation() {
    this.showJobCreationConfirmation = false;
  }

  // Add method to handle job navigation
  navigateToJobDetails() {
    if (this.jobApiRes?.result?.id) {
      const jobId = this.jobApiRes.result.id;
      // Store necessary data in localStorage
      window.localStorage.setItem('selectedJobId', jobId);
      window.localStorage.setItem('selectedJobName', this.jobApiRes.result.name);
      window.localStorage.setItem('jobCategory', 'technical');
      
      this.router.navigate(['/roles', jobId], {
        queryParams: { view: 'jobDetails' }
      });
    }
  }

  private buildPromptWithHistory(messages: { role: 'user' | 'ai', content: string }[]): string {
    if (!messages.length) return '';
    const userMessages = messages.filter(m => m.role === 'user').map(m => m.content);
    const latest = userMessages[userMessages.length - 1];
    const previous = userMessages.slice(0, -1);
    let historySummary = '';
    if (previous.length > 0) {
      // Most recent first, with [n] ... [1] ...
      const reversed = previous.slice().reverse();
      historySummary = ', Also, here is the previous conversation history: ' +
        reversed.map((msg, idx) => `[${reversed.length - idx}] ${msg}`).join(' ');
    }
    return historySummary ? `${latest}${historySummary}` : latest;
  }

  private buildDraftEditPrompt(input: string): string {
    const r = this.apiResponse || {};
    const dateOnly = (d: any) => (d ? String(d).split('T')[0] : 'Not specified');

    if (this.selectedPurpose === 'createEvent') {
      const jobRoleNames = Array.isArray(r.job_roles)
        ? r.job_roles.map((x: any) => x?.name || x?.id || x).join(', ') : '';
      const categoryName = r.event_category_id?.name || r.event_category_id || '';
      const typeName = r.event_type_id?.name || r.event_type_id || '';
      const institutionNames = Array.isArray(r.institutes)
        ? r.institutes.map((x: any) => x?.name || x?.id || x).join(', ') : '';

      return `These are the current event details. Apply the requested change and return the complete updated event, keeping every other detail unchanged unless the change requires otherwise.

Current event details:
Event Name: ${r.name || 'Not specified'}
Start Date: ${dateOnly(r.event_start_date)}
End Date: ${dateOnly(r.event_end_date)}
Registration Start Date: ${dateOnly(r.registration_start_date)}
Registration End Date: ${dateOnly(r.registration_end_date)}
Job Roles: ${jobRoleNames || 'Not specified'}
Work Experience: ${r.work_experience ? r.work_experience + ' years' : 'Not specified'}
Min Salary: ${r.min_salary || 'Not specified'}
Max Salary: ${r.max_salary || 'Not specified'}
Event Category: ${categoryName || 'Not specified'}
Event Type: ${typeName || 'Not specified'}
Institutions: ${institutionNames || 'Not specified'}
Institution Address: ${r.institution_address || 'Not specified'}

Requested change: ${input}`;
    }

    // createJob
    const loc = Array.isArray(r.locations) && r.locations.length > 0 ? r.locations[0] : {};
    const cityStr = Array.isArray(loc.city) ? loc.city.join(', ') : (loc.city || '');
    const roleTypeName = this.getRoleTypeName(r.role_type_id);
    const description = (r.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    return `These are the current job details. Apply the requested change and return the complete updated job, keeping every other detail unchanged unless the change requires otherwise.

Current job details:
Job Title: ${r.name || 'Not specified'}
Role Type: ${roleTypeName || 'Not specified'}
Work Mode: ${r.work_mode || 'Not specified'}
Country: ${loc.country || 'Not specified'}
State: ${loc.state || 'Not specified'}
City: ${cityStr || 'Not specified'}
Business Unit: ${loc.business_unit || 'Not specified'}
Open Positions: ${loc.opening ?? 'Not specified'}
Maximum Applicants: ${loc.max_applicant ?? 'Not specified'}
Description: ${description || 'Not specified'}

Requested change: ${input}`;
  }

  // Helper methods for new UI
  setSampleInput(): void {
    this.userInput = this.getSampleText();
    
    // Trigger resize after setting sample text
    // Use double setTimeout to ensure Angular change detection completes and DOM is updated
    setTimeout(() => {
      setTimeout(() => {
        const textarea = document.querySelector('.input-field-v2') as HTMLTextAreaElement;
        if (textarea) {
          this.resizeTextarea(textarea);
        }
      }, 0);
    }, 0);
  }

  getSampleText(): string {
    if (this.selectedPurpose === 'createJob') {
      return 'Create Backend Developer role - Full-time Office in Chennai, Engineering team, 3 openings, max 30 applicants, requiring Python/Java/Node.js, API development, database skills, diversity target: 12% male, 15% female, 2% other';
    } else {
      return 'Create Backend Developer Hiring Event starting next week for Software Developers with 2 years experience, salary 50000-80000, Technical category';
    }
  }

  getEventFields(): Array<{label: string, value: string}> {
    if (!this.apiResponse) return [];
    
    const fields = [
      { label: 'Event Name', value: this.apiResponse.name || 'N/A' },
      { label: 'Start Date', value: this.apiResponse.event_start_date ? new Date(this.apiResponse.event_start_date).toLocaleDateString() : 'N/A' },
      { label: 'End Date', value: this.apiResponse.event_end_date ? new Date(this.apiResponse.event_end_date).toLocaleDateString() : 'N/A' },
      { label: 'Job Roles', value: this.getJobRoles() },
      { label: 'Work Experience', value: this.apiResponse.work_experience ? `${this.apiResponse.work_experience} years` : 'N/A' },
      { label: 'Salary Range', value: this.apiResponse.min_salary && this.apiResponse.max_salary ? `${this.apiResponse.min_salary} - ${this.apiResponse.max_salary}` : 'N/A' },
      { label: 'Event Category', value: this.apiResponse.event_category_id?.name || 'N/A' },
      { label: 'Event Type', value: this.apiResponse.event_type_id?.name || 'N/A' }
    ];

    // Add institution fields only if institutions are present
    const institutionNames = this.getInstitutionNames();
    if (institutionNames && institutionNames !== 'N/A') {
      fields.push({ label: 'Institutions', value: institutionNames });
    }
    if (this.apiResponse.institution_address) {
      fields.push({ label: 'Institution Address', value: this.apiResponse.institution_address });
    }

    fields.push(
      { label: 'Registration Start', value: this.apiResponse.registration_start_date ? new Date(this.apiResponse.registration_start_date).toLocaleDateString() : 'N/A' },
      { label: 'Registration End', value: this.apiResponse.registration_end_date ? new Date(this.apiResponse.registration_end_date).toLocaleDateString() : 'N/A' }
    );
    
    return fields;
  }

  getJobFields(): Array<{label: string, value: string}> {
    if (!this.apiResponse) return [];
    
    return [
      { label: 'Job Title', value: this.apiResponse.name || 'N/A' },
      { label: 'Role Type', value: this.getRoleTypeName(this.apiResponse.role_type_id) },
      { label: 'Work Mode', value: this.apiResponse.work_mode || 'N/A' },
      { label: 'Location', value: this.apiResponse.locations && this.apiResponse.locations.length > 0 ? this.getLocationDetails(this.apiResponse.locations[0]) : 'N/A' },
      { label: 'Business Unit', value: this.apiResponse.locations && this.apiResponse.locations.length > 0 ? this.apiResponse.locations[0].business_unit : 'N/A' },
      { label: 'Description', value: this.apiResponse.description || 'N/A' },
      { label: 'Openings', value: this.apiResponse.locations && this.apiResponse.locations.length > 0 ? this.apiResponse.locations[0].opening?.toString() : 'N/A' },
      { label: 'Max Applicants', value: this.apiResponse.locations && this.apiResponse.locations.length > 0 ? this.apiResponse.locations[0].max_applicant?.toString() : 'N/A' }
    ];
  }

  getFieldIconClass(index: number): string {
    const classes = ['blue-icon', 'purple-icon', 'pink-icon'];
    return classes[index % 3];
  }

  getFieldIcon(index: number): string {
    const icons = ['pi-calendar', 'pi-clock', 'pi-check'];
    return icons[index % 3];
  }

  // Resize functionality
  startResize(event: MouseEvent): void {
    this.isResizing = true;
    event.preventDefault();
    
    document.addEventListener('mousemove', this.handleResize.bind(this));
    document.addEventListener('mouseup', this.stopResize.bind(this));
  }

  private handleResize(event: MouseEvent): void {
    if (!this.isResizing) return;
    
    const newWidth = window.innerWidth - event.clientX;
    
    // Set min and max width constraints
    if (newWidth >= 300 && newWidth <= 800) {
      this.sidebarWidth = newWidth;
    }
  }

  private stopResize(): void {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.handleResize.bind(this));
    document.removeEventListener('mouseup', this.stopResize.bind(this));
  }

  populateEditForm(): void {
    if (!this.apiResponse) return;

    if (this.selectedPurpose === 'createEvent') {
      // Convert dates to Date objects for p-calendar
      const startDate = this.apiResponse.event_start_date ? new Date(this.apiResponse.event_start_date) : null;
      const endDate = this.apiResponse.event_end_date ? new Date(this.apiResponse.event_end_date) : null;
      const regStartDate = this.apiResponse.registration_start_date ? new Date(this.apiResponse.registration_start_date) : null;
      const regEndDate = this.apiResponse.registration_end_date ? new Date(this.apiResponse.registration_end_date) : null;

      // Get job role IDs if available
      let jobRoleIds: any[] = [];
      if (this.apiResponse.job_roles && Array.isArray(this.apiResponse.job_roles)) {
        jobRoleIds = this.apiResponse.job_roles.map((role: any) => role.id || role);
      }

      // Get institution IDs if available
      let institutionIds: any[] = [];
      if (this.apiResponse.institutes && Array.isArray(this.apiResponse.institutes)) {
        institutionIds = this.apiResponse.institutes.map((inst: any) => inst.id || inst);
      }

      this.editForm.patchValue({
        eventName: this.apiResponse.name || '',
        startDate: startDate,
        endDate: endDate,
        registrationStartDate: regStartDate,
        registrationEndDate: regEndDate,
        eventCategory: this.apiResponse.event_category_id?.id || '',
        eventType: this.apiResponse.event_type_id?.id || '',
        institutionName: institutionIds,
        institutionAddress: this.apiResponse.institution_address || '',
        jobRoles: jobRoleIds,
        workExperience: this.apiResponse.work_experience || 0,
        minSalary: this.apiResponse.min_salary || 0,
        maxSalary: this.apiResponse.max_salary || 0
      });

      // Set up institution field validation based on event type
      setTimeout(() => {
        this.onEventTypeChangeForEdit();
        this.initialFormValues = this.editForm.value;
      });
    } else if (this.selectedPurpose === 'createJob') {
      const location = this.apiResponse.locations && this.apiResponse.locations.length > 0 ? this.apiResponse.locations[0] : {};
      
      // Handle city field - API might return array but form expects string
      let cityValue = '';
      if (location.city) {
        if (Array.isArray(location.city)) {
          cityValue = location.city.length > 0 ? location.city[0] : '';
        } else {
          cityValue = location.city;
        }
      }
      const countryKey = this.matchToOption(Object.keys(this.jsonData || {}), location.country);
      const stateKeys = countryKey ? Object.keys(this.jsonData[countryKey] || {}) : [];
      const stateKey = this.matchToOption(stateKeys, location.state);
      const cityKeys = (countryKey && stateKey) ? (this.jsonData[countryKey][stateKey] || []) : [];
      const cityKey = this.matchToOption(cityKeys, cityValue);

      this.editForm.patchValue({
        jobTitle: this.apiResponse.name || '',
        roleType: this.apiResponse.role_type_id || '',
        workMode: this.apiResponse.work_mode || '',
        country: countryKey,
        state: stateKey,
        city: cityKey,
        businessUnit: location.business_unit || '',
        opening: location.opening || 0,
        maxApplicant: location.max_applicant || 0,
        description: this.apiResponse.description || ''
      });

      // Load states and cities based on the resolved keys
      if (countryKey) {
        this.onCountryChangeForEdit({ value: countryKey }, true);
        if (stateKey) {
          this.onStateChangeForEdit({ value: stateKey }, true);
        }
      }

      this.initialFormValues = this.editForm.value;
    }
  }

  formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  async saveChanges(): Promise<void> {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.creationStatus = null; 

    try {
      const f = this.editForm.value;
      const updated: any = { ...this.apiResponse };

      if (this.selectedPurpose === 'createEvent') {
        updated.name = f.eventName || updated.name;
        updated.event_start_date        = this.toEventDateString(f.startDate);
        updated.event_end_date          = this.toEventDateString(f.endDate);
        updated.registration_start_date = this.toEventDateString(f.registrationStartDate);
        updated.registration_end_date   = this.toEventDateString(f.registrationEndDate);
        updated.work_experience = f.workExperience;
        updated.min_salary = f.minSalary;
        updated.max_salary = f.maxSalary;

        // Resolve dropdown ids back to full objects so the summary still shows names
        const cat = this.eventCategories.find(c => c.id === f.eventCategory);
        if (cat) updated.event_category_id = cat;
        const type = this.eventTypes.find(t => t.id === f.eventType);
        if (type) updated.event_type_id = type;

        if (Array.isArray(f.jobRoles)) {
          updated.job_roles = f.jobRoles.map((id: any) => this.jobRoles.find(r => r.id === id) || { id });
        }

        const instIds = Array.isArray(f.institutionName)
          ? f.institutionName
          : (f.institutionName ? [f.institutionName] : []);
        updated.institutes = instIds.map((id: any) => this.institutions.find(i => i.id === id) || { id });
        updated.institution_address = f.institutionAddress || null;

      } else if (this.selectedPurpose === 'createJob') {
        updated.name = f.jobTitle || updated.name;
        updated.role_type_id = f.roleType;
        updated.work_mode = f.workMode;
        updated.description = f.description;

        if (!Array.isArray(updated.locations)) updated.locations = [];
        const loc = updated.locations[0] || {};
        updated.locations[0] = {
          ...loc,
          country: f.country || '',
          state: f.state || '',
          city: f.city || '',
          business_unit: f.businessUnit || 'Demo Unit',
          opening: f.opening ?? loc.opening ?? 1,
          max_applicant: f.maxApplicant ?? loc.max_applicant ?? 10,
          diversity_male: loc.diversity_male || 0,
          diversity_female: loc.diversity_female || 0,
          diversity_other: loc.diversity_other || 0
        };
      }

      this.apiResponse = updated;
      this.isEditing = false;
      this.initialFormValues = null; // Reset initial values after successful save

      const updateMessage = `I've updated the ${this.selectedPurpose === 'createJob' ? 'job' : 'event'} details. Please review the summary and click Create when ready.`;
      this.messages.push({
        type: 'ai',
        content: updateMessage,
        safeContent: this.sanitizeMessageContent(updateMessage)
      });
    } finally {
      this.isUpdating = false;
    }
  }


  private toEventDateString(d: any): string {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00.000+05:30`;
  }

  /**
   * Handle input change and trigger resize
   */
  onInputChange(value: string): void {
    // Use setTimeout to ensure DOM is updated before resizing
    setTimeout(() => {
      const textarea = document.querySelector('.input-field-v2') as HTMLTextAreaElement;
      if (textarea) {
        this.resizeTextarea(textarea);
      }
    }, 0);
  }

  /**
   * Auto-resize textarea based on content
   */
  autoResizeTextarea(event: any): void {
    const textarea = event.target || event;
    
    // For paste events, wait a moment for the paste to complete
    if (event.type === 'paste') {
      setTimeout(() => {
        this.resizeTextarea(textarea);
      }, 0);
    } else {
      this.resizeTextarea(textarea);
    }
  }

  /**
   * Helper method to resize textarea
   */
  private resizeTextarea(textarea: HTMLTextAreaElement): void {
    if (!textarea) return;
    
    // Use requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      // Reset height to get the correct scrollHeight
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      
      // Check if mobile screen
      const isMobile = window.innerWidth < 1024;
      
      // Calculate the new height based on scrollHeight
      const maxHeight = isMobile ? 96 : 108; // Mobile: 96px (6rem), Desktop: 108px (6.75rem) - 3 lines max
      const minHeight = isMobile ? 32 : 36; // Mobile: 32px (2rem), Desktop: 36px (2.25rem) - 1 line min
      
      // Calculate new height - ensure it's between min and max
      let newHeight = scrollHeight;
      if (scrollHeight < minHeight) {
        newHeight = minHeight;
      } else if (scrollHeight > maxHeight) {
        newHeight = maxHeight;
      }
      
      // Set the height
      textarea.style.height = newHeight + 'px';
      
      // Show scrollbar only if content exceeds max height
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.overflowY = 'hidden';
      }
    });
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.editForm.reset();
    this.initialFormValues = null;
  }

  // Check if form has any changes from initial values
  hasFormChanges(): boolean {
    if (!this.initialFormValues) return false;
    
    const currentValues = this.editForm.value;
    
    // Deep comparison of form values
    return JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues);
  }

  getSelectedEventType() {
    const selectedId = this.editForm.value.eventType;
    return this.eventTypes.find(eventType => eventType.id === selectedId);
  }

  onEventTypeChangeForEdit() {
    const selectedEventType = this.getSelectedEventType();
    const institutionNameControl = this.editForm.get('institutionName');
    const institutionAddressControl = this.editForm.get('institutionAddress');
    const currentValue = institutionNameControl?.value;

    if (!institutionNameControl || !institutionAddressControl) return;

    if (selectedEventType?.institution_select_type === 'not_applicable') {
      institutionNameControl.clearValidators();
      institutionAddressControl.clearValidators();
      Promise.resolve().then(() => institutionNameControl.setValue([]));
      Promise.resolve().then(() => institutionAddressControl.setValue(''));
    } else if (selectedEventType?.institution_select_type === 'single') {
      institutionNameControl.setValidators([Validators.required]);
      institutionAddressControl.setValidators([Validators.required]);
      
      const singleValue = Array.isArray(currentValue) ? (currentValue.length > 0 ? currentValue[0] : null) : currentValue;
      Promise.resolve().then(() => institutionNameControl.setValue(singleValue));
    } else {
      // Multiple select case
      institutionNameControl.setValidators([Validators.required]);
      institutionAddressControl.setValidators([Validators.required]);
      const arrayValue = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);
      Promise.resolve().then(() => institutionNameControl.setValue(arrayValue));
    }

    institutionNameControl.updateValueAndValidity();
    institutionAddressControl.updateValueAndValidity();
  }

  private async loadFormDataForEvents(): Promise<void> {
    try {
      // Load event form data
      const eventResponse = await firstValueFrom(this.copilotService.getEventFormData());
      if (eventResponse && eventResponse.body) {
        this.eventCategories = eventResponse.body.event_categories || [];
        this.eventTypes = eventResponse.body.event_types || [];
        this.jobRoles = eventResponse.body.job_roles || [];
        this.institutions = eventResponse.body.institutes || []; // API returns 'institutes' not 'institutions'
      }
      
      // Set required validators for all mandatory event fields
      this.editForm.get('eventName')?.setValidators([Validators.required]);
      this.editForm.get('startDate')?.setValidators([Validators.required]);
      this.editForm.get('endDate')?.setValidators([Validators.required]);
      this.editForm.get('registrationStartDate')?.setValidators([Validators.required]);
      this.editForm.get('registrationEndDate')?.setValidators([Validators.required]);
      this.editForm.get('eventCategory')?.setValidators([Validators.required]);
      this.editForm.get('eventType')?.setValidators([Validators.required]);
      this.editForm.get('jobRoles')?.setValidators([Validators.required]);
      this.editForm.get('workExperience')?.setValidators([Validators.required, Validators.min(0)]);
      this.editForm.get('minSalary')?.setValidators([Validators.required, Validators.min(0)]);
      this.editForm.get('maxSalary')?.setValidators([Validators.required, Validators.min(0)]);
      
      // Update validity for all controls
      Object.keys(this.editForm.controls).forEach(key => {
        this.editForm.get(key)?.updateValueAndValidity({ emitEvent: false });
      });
      
      // Events don't need country data or form templates - only jobs do
    } catch (error) {
      // Error loading event form data - form will use empty arrays
    }
  }

  private async loadFormDataForJobs(): Promise<void> {
    try {
      // Load role types
      const roleResponse = await firstValueFrom(this.copilotService.getRoleTypes());
      if (roleResponse && roleResponse.body) {
        this.roleTypes = roleResponse.body.role_types?.map((element: any) => ({
          label: element.name,
          value: element.id
        })) || [];
      }

      // Load country data from frontend service (like event-details component)
      // Use firstValueFrom to properly wait for the data before proceeding
      const data = await firstValueFrom(this.countryStateCityService.getCountryStateCityData());
      this.jsonData = data;
      this.countries = Object.keys(this.jsonData).map(country => ({
        label: country,
        value: country
      }));
      
      // Set required validators for all mandatory job fields
      this.editForm.get('jobTitle')?.setValidators([Validators.required]);
      this.editForm.get('roleType')?.setValidators([Validators.required]);
      this.editForm.get('workMode')?.setValidators([Validators.required]);
      this.editForm.get('country')?.setValidators([Validators.required]);
      this.editForm.get('state')?.setValidators([Validators.required]);
      this.editForm.get('city')?.setValidators([Validators.required]);
      this.editForm.get('businessUnit')?.setValidators([Validators.required]);
      this.editForm.get('description')?.setValidators([Validators.required]);
      
      // Update validity for all controls
      Object.keys(this.editForm.controls).forEach(key => {
        this.editForm.get(key)?.updateValueAndValidity({ emitEvent: false });
      });
    } catch (error) {
      // Error loading job form data - form will use empty arrays
    }
  }


  onCountryChangeForEdit(event: any, skipReset: boolean = false): void {
    const country = event.value;
    this.states = [];
    this.cities = [];
    
    if (country && this.jsonData[country]) {
      this.states = Object.keys(this.jsonData[country]).map(state => ({
        label: state,
        value: state
      }));
    }
    
    // Reset dependent dropdowns only if not initial load
    if (!skipReset) {
      this.editForm.patchValue({
        state: '',
        city: ''
      });
    }
  }

  onStateChangeForEdit(event: any, skipReset: boolean = false): void {
    const state = event.value;
    const country = this.editForm.get('country')?.value;
    this.cities = [];
    
    if (state && country && this.jsonData[country] && this.jsonData[country][state]) {
      this.cities = this.jsonData[country][state].map((city: string) => ({
        label: city,
        value: city
      }));
    }
    
    // Reset city only if not initial load
    if (!skipReset) {
      this.editForm.patchValue({
        city: ''
      });
    }
  }

  private getJobRoles(): string {
    if (!this.apiResponse || !this.apiResponse.job_roles) return '';
    return Array.isArray(this.apiResponse.job_roles) 
      ? this.apiResponse.job_roles.map((role: any) => role.name || role).join(', ')
      : this.apiResponse.job_roles;
  }

  getInstitutionNames(): string {
    if (!this.apiResponse?.institutes || !Array.isArray(this.apiResponse.institutes) || this.apiResponse.institutes.length === 0) {
      return 'N/A';
    }
    
    return this.apiResponse.institutes.map((institution: any) => institution.name || institution).join(', ');
  }

  private matchToOption(options: string[], value: any): string {
    if (!value || !Array.isArray(options) || options.length === 0) return '';
    const v = String(value).trim();
    if (!v) return '';

    // exact
    let m = options.find(o => o === v);
    if (m) return m;

    // case-insensitive
    const lower = v.toLowerCase();
    m = options.find(o => o.toLowerCase() === lower);
    if (m) return m;

    // abbreviation by initials (e.g. "TN" -> "Tamil Nadu")
    if (/^[A-Za-z]{2,4}$/.test(v)) {
      const up = v.toUpperCase();
      m = options.find(o => o.split(/\s+/).map(w => w[0] || '').join('').toUpperCase() === up);
      if (m) return m;
    }

    // startsWith, then contains
    m = options.find(o => o.toLowerCase().startsWith(lower))
      || options.find(o => o.toLowerCase().includes(lower));
    return m || '';
  }
}
